import React from "react";
import { CButton, CCol, CInput, CLabel, CRow, CSelect } from "@coreui/react";
import Errors, { getFieldErrors } from "../../models/errors";
import {
  ReceiptPaymentMethod,
  RECEIPT_PAYMENT_METHOD_CHOICES,
  RECEIPT_PAYMENT_METHOD_CHECK,
} from "../../models/receipt";
import { CURRENCY_CHOICES } from "../../currency/available-currencies";
import { FieldErrors } from "../form/FieldErrors";

interface ReceiptPaymentMethodFormProps {
  payment: ReceiptPaymentMethod;
  index: number;
  errors: Errors;
  readonly?: boolean;
  onChange: (payment: ReceiptPaymentMethod) => void;
  onDelete: () => void;
}

const ReceiptPaymentMethodForm: React.FC<ReceiptPaymentMethodFormProps> = ({
  payment, index, errors, readonly, onChange, onDelete,
}) => {
  const fieldId = `receipt-payment-${index}`;
  return (
    <div
      role="group"
      aria-label={`Pago ${index + 1}`}
      style={{
        border: "1px solid var(--theme-border)", borderLeft: "4px solid #006657",
        borderRadius: "6px", padding: "12px 14px", marginBottom: "10px",
        background: "var(--theme-surface)",
      }}
    >
      <div className="d-flex justify-content-between align-items-center mb-2">
        <strong style={{ fontSize: "0.85rem", color: "var(--theme-muted)" }}>Pago {index + 1}</strong>
        <CButton
          type="button" color="danger" variant="outline" size="sm"
          aria-label={`Eliminar pago ${index + 1}`} title={`Eliminar pago ${index + 1}`}
          disabled={readonly} onClick={onDelete}
        >
          <i className="fa fa-trash" aria-hidden="true" />
        </CButton>
      </div>
      <CRow>
        <CCol xs={12} md={5} className="mb-2">
          <CLabel htmlFor={`${fieldId}-method`}><span className="text-danger">*</span> Forma de pago</CLabel>
          <CSelect
            id={`${fieldId}-method`} value={payment.paymentMethod} disabled={readonly}
            onChange={(event: React.ChangeEvent<HTMLSelectElement>) => onChange({ ...payment, paymentMethod: event.target.value })}
          >
            {Array.from(RECEIPT_PAYMENT_METHOD_CHOICES, ([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </CSelect>
          <FieldErrors errors={getFieldErrors("paymentMethod", errors) as string[]} />
        </CCol>
        <CCol xs={12} md={4} className="mb-2">
          <CLabel htmlFor={`${fieldId}-amount`}><span className="text-danger">*</span> Monto pagado</CLabel>
          <CInput
            id={`${fieldId}-amount`} type="number" min="0.01" step="0.01"
            placeholder="Monto" value={payment.amount || ""} disabled={readonly}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange({ ...payment, amount: Number(event.target.value) })}
          />
          <FieldErrors errors={getFieldErrors("amount", errors) as string[]} />
        </CCol>
        <CCol xs={12} md={3} className="mb-2">
          <CLabel htmlFor={`${fieldId}-currency`}><span className="text-danger">*</span> Moneda</CLabel>
          <CSelect
            id={`${fieldId}-currency`} value={payment.currency} disabled={readonly}
            onChange={(event: React.ChangeEvent<HTMLSelectElement>) => onChange({ ...payment, currency: event.target.value })}
          >
            {Array.from(CURRENCY_CHOICES, ([value, label]) => <option key={value} value={value}>{label}</option>)}
          </CSelect>
          <FieldErrors errors={getFieldErrors("currency", errors) as string[]} />
        </CCol>
      </CRow>
      <CRow>
        <CCol xs={12} className="mb-2">
          <CLabel htmlFor={`${fieldId}-description`}>Descripción del pago</CLabel>
          <CInput
            id={`${fieldId}-description`} value={payment.description ?? ""} maxLength={255} disabled={readonly}
            placeholder="Referencia o detalle del pago"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange({ ...payment, description: event.target.value })}
          />
          <FieldErrors errors={getFieldErrors("description", errors) as string[]} />
        </CCol>
      </CRow>
      {payment.paymentMethod === RECEIPT_PAYMENT_METHOD_CHECK && (
        <CRow>
          <CCol xs={12} md={6}>
            <CLabel htmlFor={`${fieldId}-check-number`}>Nº Cheque (opcional)</CLabel>
            <CInput
              id={`${fieldId}-check-number`} value={payment.checkNumber ?? ""} maxLength={255} disabled={readonly}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange({ ...payment, checkNumber: event.target.value })}
            />
            <FieldErrors errors={getFieldErrors("checkNumber", errors) as string[]} />
          </CCol>
          <CCol xs={12} md={6}>
            <CLabel htmlFor={`${fieldId}-check-bank`}>Banco Cheque (opcional)</CLabel>
            <CInput
              id={`${fieldId}-check-bank`} value={payment.checkBank ?? ""} maxLength={255} disabled={readonly}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange({ ...payment, checkBank: event.target.value })}
            />
            <FieldErrors errors={getFieldErrors("checkBank", errors) as string[]} />
          </CCol>
        </CRow>
      )}
    </div>
  );
};

export default ReceiptPaymentMethodForm;
